import React, { ReactNode, useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import { useLocation } from "react-router-dom";
import PageHeader from "../../../app/layout/PageHeader";
import { api } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { notify } from "../../components-ui/toast";
import OrganisationDetailsStep from "./steps/OrganisationDetailsStep";
import OrganisationPeoplePage from "./OrganisationPeoplePage";
import OrganisationProfileStep from "./steps/OrganisationProfileStep";
import "../../../css/buttons.css";
import "./tenant-administration.css";

interface TenantAdministration {
  tenantId: number;
  name: string;
  slug: string;
  description?: string;
  isActive: boolean;
  partyId: number;
}

type OrganisationStep = "details" | "people" | "profile";

const BASE_ORGANISATION_STEPS: Array<{
  key: OrganisationStep;
  label: string;
}> = [
  { key: "details", label: "Organisation details" },
  { key: "people", label: "Organisation people" },
];

export default function TenantAdministrationPage() {
  const { isSystemAdmin } = useAuth();
  const location = useLocation();
  const [tenants, setTenants] = useState<TenantAdministration[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [peopleHeaderAction, setPeopleHeaderAction] = useState<ReactNode | null>(
    null,
  );
  const [activeStep, setActiveStep] = useState<OrganisationStep>("details");

  const organisationSteps = isSystemAdmin
    ? [
        ...BASE_ORGANISATION_STEPS,
        { key: "profile" as OrganisationStep, label: "Profile" },
      ]
    : BASE_ORGANISATION_STEPS;

  const loadTenants = useCallback(async (preferredTenantId?: number) => {
    try {
      const result = await api<TenantAdministration[]>(
        "/api/tenant-administration",
      );
      setTenants(result);
      setSelectedTenantId((current) => {
        const requested = preferredTenantId ?? current;
        return result.some((tenant) => tenant.tenantId === requested)
          ? requested!
          : result[0]?.tenantId ?? null;
      });
    } catch (error) {
      console.error("Failed to load tenants", error);
      notify.error("Failed to load organisation details");
    }
  }, []);

  useEffect(() => {
    const requestedTenantId = Number(
      new URLSearchParams(location.search).get("tenantId"),
    );
    void loadTenants(requestedTenantId || undefined);
  }, [loadTenants, location.search]);

  const selectedTenant = tenants.find(
    (tenant) => tenant.tenantId === selectedTenantId,
  );
  const activeStepIndex = organisationSteps.findIndex(
    (step) => step.key === activeStep,
  );

  const goToStep = (stepIndex: number) => {
    if (stepIndex > 0 && !selectedTenant) return;
    const step = organisationSteps[stepIndex];
    if (step) setActiveStep(step.key);
  };

  return (
    <div className="tenant-admin-page">
      <PageHeader
        title="Organisation management"
        subtitle={
          isSystemAdmin
            ? "Manage organisation details, people, and profiles."
            : "Manage your organisation."
        }
        actions={
          activeStep === "people" ? peopleHeaderAction : undefined
        }
      />

      <Box
        className="tenant-admin-desktop-stepper"
        sx={{ width: "100%", mb: 3 }}
      >
        <Stepper activeStep={activeStepIndex} alternativeLabel>
          {organisationSteps.map((step, index) => (
            <Step key={step.key}>
              <StepButton
                onClick={() => goToStep(index)}
                disabled={index > 0 && !selectedTenant}
                sx={{
                  "& .MuiStepLabel-label": {
                    color:
                      index > 0 && !selectedTenant ? "#9ca3af" : "inherit",
                  },
                }}
              >
                {step.label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      <div className="tenant-admin-mobile-stepper">
        <label className="tenant-admin-mobile-step-select">
          <select
            value={activeStepIndex}
            onChange={(event) => goToStep(Number(event.target.value))}
            aria-label="Go to organisation management step"
          >
            {organisationSteps.map((step, index) => (
              <option
                key={step.key}
                value={index}
                disabled={index > 0 && !selectedTenant}
              >
                {index + 1}. {step.label}
              </option>
            ))}
          </select>
        </label>
        <span className="tenant-admin-mobile-step-count">
          Step {activeStepIndex + 1}/{organisationSteps.length}
        </span>
        <div
          className="tenant-admin-mobile-progress"
          role="progressbar"
          aria-label="Organisation management progress"
          aria-valuemin={1}
          aria-valuemax={organisationSteps.length}
          aria-valuenow={activeStepIndex + 1}
        >
          <span
            style={{
              width: `${
                ((activeStepIndex + 1) / organisationSteps.length) * 100
              }%`,
            }}
          />
        </div>
      </div>

      {activeStep === "details" &&
        (selectedTenant ? (
          <OrganisationDetailsStep
            tenantId={selectedTenant.tenantId}
            partyId={selectedTenant.partyId}
            organisations={isSystemAdmin ? tenants : [selectedTenant]}
            onOrganisationSelect={setSelectedTenantId}
            onSaved={() => void loadTenants(selectedTenant.tenantId)}
          />
        ) : (
          <div className="card tenant-admin-empty">
            {isSystemAdmin
              ? "No organisations have been created yet."
              : "No organisation is assigned to this account."}
          </div>
        ))}

      {activeStep === "people" && selectedTenant && (
        <OrganisationPeoplePage
          embedded
          tenantId={selectedTenant.tenantId}
          onHeaderActionChange={setPeopleHeaderAction}
        />
      )}

      {activeStep === "profile" && isSystemAdmin && selectedTenant && (
        <OrganisationProfileStep tenantId={selectedTenant.tenantId} />
      )}
    </div>
  );
}
